import type { AttachmentKey } from "./AiChatSidebar";
import { AttachmentChip, AttachmentIndicator } from "./AttachmentChip";
import type { AttachmentChipsProps } from "./AttachmentChips";
import {
  AttachmentBar,
  AttachmentLabel,
  truncateName,
} from "./attachmentChipStyles";

// The follows-view bar (Figma Bridge_Sidebar 15949-168524), labelled
// "Attach" like the pick-what-to-send bar: only what exists, on by
// default, a click hides it from the helper — the chip's outlined state
// says so, the tooltip explains.
function seeingItems(
  p: AttachmentChipsProps,
): Array<{ key: AttachmentKey; label: string }> {
  const items: Array<{ key: AttachmentKey; label: string }> = [];
  if (p.hasInput) items.push({ key: "input", label: "Input" });
  for (const s of p.scripts) {
    items.push({ key: `script:${s.name}`, label: truncateName(s.name) });
  }
  if (p.hasSchema) items.push({ key: "schema", label: "Schema" });
  if (p.hasTests) {
    const failed = p.testFailures > 0 ? ` (${p.testFailures} failed)` : "";
    items.push({ key: "tests", label: `Tests${failed}` });
  }
  if (p.hasPipeline) items.push({ key: "pipeline", label: "Pipeline" });
  return items;
}

// The pipeline chip says when the code is going as text: it does not
// parse, so the helper gets the code as written with the parse error.
function chipTitle(shown: boolean, unparsed: boolean): string {
  if (!shown) return "Hidden — click to send again";
  if (unparsed) {
    return "The code doesn't parse — sent as written, with the error; click to hide from the helper";
  }
  return "Sent with your messages — click to hide from the helper";
}

export function SeeingChips(p: AttachmentChipsProps) {
  return (
    <AttachmentBar data-testid="ai-seeing-bar">
      <AttachmentLabel>Attach</AttachmentLabel>
      {seeingItems(p).map(({ key, label }) => {
        const shown = p.attachments.has(key);
        const cut = p.truncated?.has(key) ? " (truncated)" : "";
        return (
          <AttachmentChip
            key={key}
            label={`${label}${cut}`}
            selected={shown}
            error={key === "tests" && shown && p.testFailures > 0}
            onClick={() => p.onToggle(key)}
            title={chipTitle(shown, key === "pipeline" && !!p.pipelineUnparsed)}
            testId={`ai-seeing-${key}`}
          />
        );
      })}
      {p.hasError && (
        <AttachmentIndicator
          label="Error"
          title="Error will be sent automatically"
        />
      )}
    </AttachmentBar>
  );
}
